import type { Settings } from "./schema";

type CrosshairSettings = Settings["crosshair"];

/**
 * Draw the crosshair centered on (cx, cy) in CSS pixels. Used by the settings preview and
 * available to the in-game HUD so both render identically.
 *
 * @param extraGap extra distance of the arms from the center (spread bloom when `dynamic`)
 * @param scale multiplies every dimension (hud.scale)
 */
export function drawCrosshair(
  ctx: CanvasRenderingContext2D,
  c: CrosshairSettings,
  cx: number,
  cy: number,
  extraGap = 0,
  scale = 1,
): void {
  const gap = (c.gap + (c.dynamic ? extraGap : 0)) * scale;
  const size = c.size * scale;
  const t = c.thickness * scale;
  const o = c.outline ? c.outlineThickness * scale : 0;
  const dotR = t; // diameter thickness * 2
  const drawArms = c.style === "cross" || c.style === "crossdot";
  const drawDot = c.style === "dot" || c.style === "crossdot";

  ctx.save();
  ctx.globalAlpha = c.opacity;

  const arm = (x: number, y: number, w: number, h: number) => {
    if (o > 0) {
      ctx.fillStyle = "#000000";
      ctx.fillRect(x - o, y - o, w + 2 * o, h + 2 * o);
    }
    ctx.fillStyle = c.color;
    ctx.fillRect(x, y, w, h);
  };

  if (drawArms && size > 0) {
    arm(cx - t / 2, cy - gap - size, t, size); // up
    arm(cx - t / 2, cy + gap, t, size); // down
    arm(cx - gap - size, cy - t / 2, size, t); // left
    arm(cx + gap, cy - t / 2, size, t); // right
  }

  if (drawDot) {
    if (o > 0) {
      ctx.fillStyle = "#000000";
      ctx.beginPath();
      ctx.arc(cx, cy, dotR + o, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = c.color;
    ctx.beginPath();
    ctx.arc(cx, cy, dotR, 0, Math.PI * 2);
    ctx.fill();
  }

  if (c.style === "circle") {
    const r = (c.gap + (c.dynamic ? extraGap : 0) + c.size) * scale;
    if (o > 0) {
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = t + 2 * o;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.strokeStyle = c.color;
    ctx.lineWidth = t;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.restore();
}

/** Classic dot sight red. */
export const DOT_SIGHT_RED = "#ff2a2a";

/**
 * The reticle of a dot sight, drawn while aiming through a red dot or holo: a dot of 2.4 px
 * radius, red by default or in the crosshair colour when `dotColor` is "crosshair", with a dark
 * ring (readable on bright walls and sky) and a soft glow in the dot colour (readable on dark
 * ones). It stays opaque whatever the crosshair opacity says, because a dot sight that fades out
 * is hard to use.
 */
export function drawDotReticle(ctx: CanvasRenderingContext2D, c: CrosshairSettings, cx: number, cy: number, scale = 1): void {
  const r = 2.4 * scale;
  const ring = 1.4 * scale;
  const color = c.dotColor === "crosshair" ? c.color : DOT_SIGHT_RED;
  ctx.save();
  ctx.globalAlpha = Math.max(c.opacity, 0.9);
  ctx.fillStyle = "#000000";
  ctx.beginPath();
  ctx.arc(cx, cy, r + ring, 0, Math.PI * 2);
  ctx.fill();
  ctx.shadowColor = color;
  ctx.shadowBlur = 7 * scale;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}
